import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@app/store/authStore';
import { useChannel } from '@app/realtime';

interface IncomingCallPayload {
  consultation_id: string;
  room_id: string;
  patient_id: string;
  patient_name: string;
  appointment_id: string;
  ringing_since: string;
}

/**
 * App-wide floating banner that listens on the authenticated user's private channel
 * for IncomingCall events. Intended for clinicians; harmless for other roles
 * (the event will simply never arrive on their channel).
 *
 * Mount once inside DashboardLayout.
 */
export default function IncomingCallBanner() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [call, setCall] = useState<IncomingCallPayload | null>(null);

  useChannel(
    user ? `user.${user.id}` : '',
    {
      'incoming-call': (payload: IncomingCallPayload) => setCall(payload),
    },
    { type: 'private' },
  );

  if (!call) return null;

  const handleAccept = () => {
    navigate(`/clinician/consultations/${call.consultation_id}`);
    setCall(null);
  };

  const handleDecline = () => {
    setCall(null);
  };

  return (
    <div
      role="alertdialog"
      aria-label="Incoming consultation call"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        width: 320,
        borderRadius: 'var(--radius-lg, 12px)',
        background: 'white',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        border: '2px solid var(--heka-primary-500, #0070f3)',
        overflow: 'hidden',
        animation: 'slideInBanner 0.3s ease',
      }}
    >
      <style>{`
        @keyframes slideInBanner {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          background: 'var(--heka-primary-500, #0070f3)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {/* Pulsing call icon */}
        <span
          style={{ fontSize: 22, animation: 'pulse 1s infinite' }}
          aria-hidden="true"
        >
          📞
        </span>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
        <div>
          <div style={{ color: 'white', fontWeight: 700, fontSize: 14 }}>Incoming Call</div>
          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>
            Consultation starting
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '14px 16px' }}>
        <p
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--heka-gray-900, #111)',
            marginBottom: 4,
          }}
        >
          {call.patient_name}
        </p>
        <p style={{ fontSize: 12, color: 'var(--heka-gray-500, #6b7280)', marginBottom: 14 }}>
          Patient is waiting for you to join
        </p>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={handleAccept}
            style={{
              flex: 1,
              padding: '9px 0',
              background: 'var(--heka-primary-500, #0070f3)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md, 8px)',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Accept
          </button>
          <button
            onClick={handleDecline}
            style={{
              flex: 1,
              padding: '9px 0',
              background: 'var(--heka-gray-100, #f3f4f6)',
              color: 'var(--heka-gray-700, #374151)',
              border: 'none',
              borderRadius: 'var(--radius-md, 8px)',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}
